import type { ManagerialDashboardMeasurements } from '@/types/api'
import { formatMeasurement } from '../../lib/measurements'
import styles from './ExecutiveFinance.module.css'

export function MeasuredInsights({
  data,
  isLoading,
  windows,
}: {
  data: ManagerialDashboardMeasurements | undefined
  isLoading: boolean
  windows?: Record<string, { from: string; to: string }>
}) {
  if (!data) {
    return (
      <p
        className={isLoading ? styles.scopeNote : styles.unavailableNotice}
        role="status"
      >
        {isLoading
          ? 'Carregando medições gerenciais...'
          : 'Medições adicionais indisponíveis. Atualize os dados para consultar a base de indicadores.'}
      </p>
    )
  }
  return (
    <section
      className={styles.metrics}
      aria-label="Medições gerenciais"
      aria-busy={isLoading}
    >
      <p className={styles.scopeNote}>
        Consolidado da plataforma · independente dos filtros do topo.{' '}
        {windows
          ? 'A janela de cada grupo é informada abaixo.'
          : `Janela de 12 meses: ${data.date_from.split('-').reverse().join('/')} a ${data.date_to.split('-').reverse().join('/')}.`}{' '}
        Valores sem base de cálculo aparecem em vermelho. Zero representa um resultado
        medido nos registros.
      </p>
      {data.sections.map((section) => (
        <section key={section.id} aria-label={section.title}>
          <div className={styles.sectionHeading}>
            <h2>{section.title}</h2>
            <span>{section.period}</span>
            {windows?.[section.id] ? (
              <span>
                Janela-base: {windows[section.id].from.split('-').reverse().join('/')} a{' '}
                {windows[section.id].to.split('-').reverse().join('/')}
              </span>
            ) : null}
          </div>
          <div className={styles.metricGrid}>
            {section.metrics.map((metric) => {
              const unavailable = metric.value === null
              return (
                <article
                  key={metric.id}
                  className={`${styles.metric} ${unavailable ? styles.unavailable : ''}`}
                  aria-label={`${metric.label}: ${unavailable ? 'Sem base' : formatMeasurement(metric)}`}
                >
                  <div className={styles.metricHeading}>
                    <h3>{metric.label}</h3>
                  </div>
                  <strong
                    className={
                      unavailable || (metric.value ?? 0) < 0
                        ? styles.negative
                        : styles.measured
                    }
                  >
                    {formatMeasurement(metric)}
                  </strong>
                  {unavailable ? (
                    <p className={styles.unavailableLabel}>Sem base de cálculo</p>
                  ) : null}
                  <p className={styles.metricNote}>{metric.note}</p>
                  <details className={styles.source}>
                    <summary>Origem dos dados</summary>
                    {metric.source}
                  </details>
                </article>
              )
            })}
          </div>
        </section>
      ))}
    </section>
  )
}
